import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ConfirmDialog } from './ConfirmDialog';
import { Edit2, Save, X, MapPin, Languages, CheckCircle, AlertCircle } from 'lucide-react';
import { Family, UpdateFamilyRequest } from '../types/family.types';
import { UserStatus } from '../types/enums';
import { useFamilyMutations } from '../hooks/useFamilyMutations';

interface EditableFamilyDetailsProps {
  family: Family;
}

export const EditableFamilyDetails = ({ family }: EditableFamilyDetailsProps) => {
  const { t } = useTranslation('registration');
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState({
    postalCodePrefix: family.postalCodePrefix || '',
    isLowIncome: family.demographics.isLowIncome || false,
    languages: family.languages || [],
  });
  const [formErrors, setFormErrors] = useState<Map<string, string>>(new Map());

  const { updateFamily } = useFamilyMutations(family.id);

  const getErrorClass = (id: string) => (formErrors.has(id) ? 'border-red-500 bg-red-50/10' : '');

  const getErrorMessage = (id: string) => formErrors.get(id) || '';

  const scrollToError = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const errorBanner = document.getElementById('error-banner-family');
      if (errorBanner) {
        errorBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        element.focus({ preventScroll: true });
      }, 300);
    }
  };

  const validateForm = (): { isValid: boolean; firstErrorId?: string } => {
    const errors = new Map<string, string>();
    let firstErrorId: string | undefined;

    const addError = (id: string, message: string) => {
      errors.set(id, message);
      if (!firstErrorId) firstErrorId = id;
    };

    // Postal code validation: must be format A1A (letter, digit, letter)
    // Postal code is optional, but if provided must be valid
    if (formData.postalCodePrefix && formData.postalCodePrefix.length > 0) {
      if (!/^[A-Z][0-9][A-Z]$/.test(formData.postalCodePrefix)) {
        addError('postalCodePrefix', t('validation.invalidPostalCodeFormat'));
      }
    }

    setFormErrors(errors);
    return { isValid: errors.size === 0, firstErrorId };
  };

  // Sync form data when family prop changes (after successful save)
  useEffect(() => {
    setFormData({
      postalCodePrefix: family.postalCodePrefix || '',
      isLowIncome: family.demographics.isLowIncome || false,
      languages: family.languages || [],
    });
  }, [family]);

  const handleSave = async () => {
    const { isValid, firstErrorId } = validateForm();
    if (!isValid) {
      if (firstErrorId) scrollToError(firstErrorId);
      return;
    }

    if (family.status === UserStatus.PENDING) {
      const apiErrors = new Map<string, string>();
      apiErrors.set('postalCodePrefix', t('toast.familyPendingCannotEdit'));
      setFormErrors(apiErrors);
      scrollToError('postalCodePrefix');
      return;
    }

    const updateData: UpdateFamilyRequest = {};

    if (formData.postalCodePrefix !== family.postalCodePrefix) {
      updateData.postalCodePrefix = formData.postalCodePrefix || undefined;
    }
    if (formData.isLowIncome !== family.demographics.isLowIncome) {
      updateData.isLowIncome = formData.isLowIncome;
    }
    if (JSON.stringify(formData.languages) !== JSON.stringify(family.languages)) {
      updateData.languages = formData.languages;
    }

    if (Object.keys(updateData).length > 0) {
      try {
        await updateFamily.mutateAsync(updateData);
        setFormErrors(new Map());
      } catch (error) {
        const apiErrors = new Map<string, string>();
        const errorMsg =
          error instanceof Error ? error.message : String((error as Record<string, unknown>)?.message ?? '');

        if (errorMsg.includes('Postal') || errorMsg.includes('postal')) {
          apiErrors.set('postalCodePrefix', errorMsg);
        } else if (errorMsg) {
          apiErrors.set('postalCodePrefix', errorMsg);
        } else {
          apiErrors.set('postalCodePrefix', t('toast.failedToUpdateFamily'));
        }

        setFormErrors(apiErrors);
        if (apiErrors.size > 0) {
          const firstErrorId = apiErrors.keys().next().value as string;
          scrollToError(firstErrorId);
        }
        return;
      }
    } else {
      setFormErrors(new Map());
    }
    setIsEditing(false);
  };

  const hasChanges = () => {
    return (
      formData.postalCodePrefix !== (family.postalCodePrefix || '') ||
      formData.isLowIncome !== (family.demographics.isLowIncome || false) ||
      JSON.stringify(formData.languages) !== JSON.stringify(family.languages || [])
    );
  };

  const handleCancelClick = () => {
    if (hasChanges()) {
      setShowConfirmDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleConfirmDiscard = () => {
    setFormData({
      postalCodePrefix: family.postalCodePrefix || '',
      isLowIncome: family.demographics.isLowIncome || false,
      languages: family.languages || [],
    });
    setShowConfirmDialog(false);
    setIsEditing(false);
  };

  const getLanguageDisplay = (code: string) => t(`languages.${code}`);

  if (!isEditing) {
    return (
      <Card className="mb-6">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 pb-2">
          <CardTitle className="text-base sm:text-lg font-medium">{t('profile.familyDetails')}</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="shrink-0">
            <Edit2 className="h-4 w-4 mr-1.5" />
            {t('profile.edit')}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[var(--color-neutral-500)] shrink-0" />
              <span className="text-sm text-[var(--color-neutral-700)]">
                {t('profile.postalCode')}:{' '}
                <span className="font-medium text-[var(--color-neutral-900)]">
                  {family.postalCodePrefix || t('profile.notSet')}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[var(--color-neutral-700)]">
                {t('profile.lowIncome')}:{' '}
                <Badge variant={family.demographics.isLowIncome ? 'default' : 'secondary'}>
                  {family.demographics.isLowIncome ? t('binaryOptions.yes') : t('binaryOptions.no')}
                </Badge>
              </span>
            </div>
            <div className="flex items-start gap-2 sm:col-span-2">
              <Languages className="h-4 w-4 text-[var(--color-neutral-500)] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <span className="text-sm text-[var(--color-neutral-700)]">{t('profile.languages')}: </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {family.languages && family.languages.length > 0 ? (
                    family.languages.map((lang) => (
                      <Badge key={lang} variant="outline">
                        {getLanguageDisplay(lang)}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-[var(--color-neutral-500)]">{t('profile.notSpecified')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 ring-2 ring-[var(--color-primary-500)]">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0 pb-2">
        <CardTitle className="text-base sm:text-lg font-medium">{t('profile.editFamilyDetails')}</CardTitle>
        <div className="flex gap-2 self-end sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancelClick}
            disabled={updateFamily.isPending}
            className="shrink-0"
          >
            <X className="h-4 w-4 mr-1" />
            {t('profile.cancel')}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updateFamily.isPending} className="shrink-0">
            <Save className="h-4 w-4 mr-1" />
            {updateFamily.isPending ? t('profile.saving') : t('profile.save')}
          </Button>
        </div>
      </CardHeader>

      {formErrors.size > 0 && (
        <div
          id="error-banner-family"
          className="bg-red-50 border-l-4 border-red-500 mx-4 p-3 sm:p-4 rounded-r-lg mb-4 shadow-md sticky top-0 z-10"
          role="alert"
          aria-live="polite"
        >
          <div className="flex gap-2 sm:gap-3">
            <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-xs sm:text-sm md:text-base font-bold text-red-800 mb-2">
                {t('validation.familyValidationFailed')}
              </h3>
              <div className="text-xs sm:text-sm text-red-700 space-y-1">
                {Array.from(formErrors.values())
                  .slice(0, 5)
                  .map((error, idx) => (
                    <div key={idx} className="flex items-start gap-1.5 break-words">
                      <span className="text-red-500 font-bold mt-0.5">•</span>
                      <span>{error}</span>
                    </div>
                  ))}
                {formErrors.size > 5 && (
                  <div className="text-xs sm:text-sm text-red-700 mt-2">
                    {t('validation.andMore', { count: formErrors.size - 5 })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={t('profile.discardChanges')}
        message={t('profile.discardConfirm')}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowConfirmDialog(false)}
      />

      <CardContent>
        <div className="space-y-5">
          <div>
            <Label htmlFor="postalCodePrefix" className="text-sm font-medium">
              {t('profile.postalCodePrefix')}
            </Label>
            <Input
              id="postalCodePrefix"
              value={formData.postalCodePrefix}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  postalCodePrefix: e.target.value.toUpperCase().slice(0, 3),
                })
              }
              placeholder={t('placeholders.postalCode')}
              maxLength={3}
              className={`mt-1.5 w-full sm:max-w-[140px] text-base ${getErrorClass('postalCodePrefix')}`}
            />
            {getErrorMessage('postalCodePrefix') && (
              <div className="mt-1 flex items-start gap-1.5">
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{getErrorMessage('postalCodePrefix')}</p>
              </div>
            )}
            {!getErrorMessage('postalCodePrefix') && (
              <p className="text-xs text-[var(--color-neutral-500)] mt-1">{t('profile.postalCodeHint')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('profile.lowIncomeHousehold')}</Label>
            <div className="grid grid-cols-2 gap-3 max-w-[280px]">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, isLowIncome: true })}
                className={`p-3 sm:p-4 rounded-xl border-2 text-sm font-medium transition-all active:scale-95 ${
                  formData.isLowIncome
                    ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-100)] text-[var(--color-primary-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <CheckCircle
                  className={`h-5 w-5 mx-auto mb-1.5 ${formData.isLowIncome ? 'text-[var(--color-primary-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.yes')}
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, isLowIncome: false })}
                className={`p-3 sm:p-4 rounded-xl border-2 text-sm font-medium transition-all active:scale-95 ${
                  !formData.isLowIncome
                    ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-100)] text-[var(--color-primary-700)]'
                    : 'border-[var(--color-neutral-300)] bg-white text-[var(--color-neutral-700)] hover:border-[var(--color-neutral-400)]'
                }`}
              >
                <X
                  className={`h-5 w-5 mx-auto mb-1.5 ${!formData.isLowIncome ? 'text-[var(--color-primary-600)]' : 'text-[var(--color-neutral-400)]'}`}
                />
                {t('binaryOptions.no')}
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
